/* eslint-disable no-unused-vars */
import { CustomApiError } from 'models/error-message';
import { StringHelper } from '../../common/helpers/string-helper';
import axiosInstance from './axios.interceptors';
import axios from 'axios';

export interface IApiService {
	Get<T>(baseUri: string, endpointUri: string): Promise<T>;
	Post<T>(baseUri: string, endpointUri: string, payload: any, responseType: 'json' | 'blob'): Promise<T>;
	Put<T>(baseUri: string, endpointUri: string, payload: any): Promise<T>;
	Delete<T>(baseUri: string, endpointUri: string): Promise<T>;
	PostStream(baseUri: string, endpointUri: string, payload: any, onChunk: (chunk: string) => void, onError?: (error: Error) => void, onComplete?: () => void): Promise<void>;
}

export class ApiService implements IApiService {
	public async Get<T>(baseUri: string, endpointUri: string): Promise<T> {
		const targetUri = `${baseUri}/${endpointUri}`;
		try {
			let result = await axiosInstance.get<T>(`${targetUri}`);
			return result.data;
		} catch (err: any) {
			if (axios.isAxiosError(err)) {
                const apiError = err.response?.data as CustomApiError;
                if (apiError && apiError.Messages) {
                    throw new Error(apiError.Messages.join(', '));
                }
                throw new Error(err.message);
            }
			throw new Error(err?.toString());
		}
	}

	public async Post<T>(
		baseUri: string,
		endpointUri: string,
		payload: any = {},
		responseType: 'json' | 'blob' = 'json'
	): Promise<T> {
		const targetUri = `${baseUri}/${endpointUri}`;

		try {
			let result = await axiosInstance.post<T>(`${targetUri}`, payload, { responseType: responseType });
			return result.data;
		} catch (err:any) {
			if (axios.isAxiosError(err)) {
                const apiError = err.response?.data as CustomApiError;
                if (apiError && apiError.Messages) {
                    throw new Error(apiError.Messages.join(', '));
                }
                throw new Error(err.message);
            }
		   throw new Error(err?.toString());
		}
	}

	public async Put<T>(
		baseUri: string,
		endpointUri: string,
		payload: any
	): Promise<T> {
		const targetUri = `${baseUri}/${endpointUri}`;

		try {
			let result = await axiosInstance.put<T>(`${targetUri}`, payload);
			return result.data;
		} catch (err: any) {
			if (axios.isAxiosError(err)) {
                const apiError = err.response?.data as CustomApiError;
                if (apiError && apiError.Messages) {
                    throw new Error(apiError.Messages.join(', '));
                }
                throw new Error(err.message);
            }
		   throw new Error(err?.toString());
		}
	}

	public async Delete<T>(
		baseUri: string,
		endpointUri: string
	): Promise<T> {
		const targetUri = `${baseUri}/${endpointUri}`;

		try {
			let result = await axiosInstance.delete<T>(`${targetUri}`);
			return result.data;
		} catch (err: any) {
			if (axios.isAxiosError(err)) {
                const apiError = err.response?.data as CustomApiError;
                if (apiError && apiError.Messages) {
                    throw new Error(apiError.Messages.join(', '));
                }
                throw new Error(err.message);
            }
		   throw new Error(err?.toString());
		}
	}

	public async PostStream(
		baseUri: string,
		endpointUri: string,
		payload: any,
		onChunk: (chunk: string) => void,
		onError?: (error: Error) => void,
		onComplete?: () => void
	): Promise<void> {
		const targetUri = `${baseUri}/${endpointUri}`;

		try {
			
			// Get token using the existing MSAL instance - NO extra API calls
			let authToken = '';
			try {
				// Import the existing MSAL instance and config
				const { msalInstance } = await import('../../index');
				const { loginRequest } = await import('../../authConfig');
				
				const accounts = msalInstance?.getAllAccounts();
				if (accounts && accounts.length > 0) {
					const response = await msalInstance.acquireTokenSilent({
						scopes: loginRequest.scopes,
						account: accounts[0],
					});
					authToken = `Bearer ${response.accessToken}`;
				}
			} catch (tokenError) {
				// Token acquisition failed, continue without token
			}
			
			// Use fetch for TRUE streaming with the token
			const response = await fetch(targetUri, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'text/plain, text/event-stream',
					'Cache-Control': 'no-cache',
					...(authToken && { 'Authorization': authToken })
				},
				body: JSON.stringify(payload)
			});

			if (!response.ok) {

				if(response.status === 406) {
					const authError = new Error('Your question could not be processed. Please rephrase your request and try again.');
					onError?.(authError);
					throw authError;
				}
				const errorText = await response.text();
				throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
			}

			if (!response.body) {
				throw new Error('Response body is null');
			}

			// Process REAL streaming chunks - SSE FORMAT VERSION
			const reader = response.body.getReader();
			const decoder = new TextDecoder();
			let chunkCount = 0;
			const startTime = Date.now();
			let totalBytes = 0;
			let buffer = '';

			try {
				while (true) {
					const { done, value } = await reader.read();
					
					if (done) {
						break;
					}

					chunkCount++;
					totalBytes += value.length;
					const chunk = decoder.decode(value, { stream: true });
					buffer += chunk;
					
					// Process complete SSE messages (ending with \n\n)
					const lines = buffer.split('\n');
					buffer = lines.pop() || ''; // Keep incomplete line in buffer
					
					for (const line of lines) {
						if (line.trim() === '') continue; // Skip empty lines
						
						// Check if this is a data line
						if (line.startsWith('data: ')) {
							const data = line.substring(6); // Remove "data: " prefix
							
							if (data.trim()) {
								try {
									// Try to parse as JSON
									const parsedChunk = JSON.parse(data);
									//console.log('Parsed SSE chunk:', parsedChunk);
									
									// Call onChunk with parsed object
									onChunk(parsedChunk);
								} catch (parseError) {
									// If not JSON, treat as plain text
									console.log('SSE chunk (text):', data);
									onChunk(data);
								}
								
								// Add a small delay to show the streaming effect
								await new Promise(resolve => setTimeout(resolve, 200));
							}
						}
					}
				}
			} finally {
				reader.releaseLock();
			}
			
			onComplete?.();

		} catch (err: any) {
			const error = new Error(err.message || 'Streaming request failed');
			onError?.(error);
			throw error;
		}
	}
}
